import { useEffect, useRef, useState, type CSSProperties, type SyntheticEvent } from 'react';

import { Alert, FlexAligner } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { Loader } from '@/components';
import { colors } from '@/utils';

import { resolveAnnotationBoxes } from './fishObservationGallery.functions.ts';
import {
  FishObservationSlide,
  type OrbitOneGalleryFilterClass,
} from './fishObservationGallery.types.ts';
import { OrbitOneAnnotationOverlay } from './fishObservationSlideView/OrbitOneAnnotationOverlay.tsx';

// Image and overlay share this box and both `contain` into it (object-fit / preserveAspectRatio
// meet), so they stay aligned even when the box is wider/taller than the image (fullscreen, ultra-wide).
const imageBoxStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
};

const imageStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const fullResolutionErrorStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
};

type FishObservationSlideViewProps = {
  slide: FishObservationSlide;
  /** Live lightbox zoom level, forwarded to the overlay so its strokes thin out on zoom. */
  zoom?: number;
  /** Detection classes to draw boxes for. */
  annotationTypes: OrbitOneGalleryFilterClass[];
};

/**
 * Renders a fish-crop slide with its lice-annotation overlay. Returned from the lightbox
 * `render.slide`, so it is mounted inside the zoom plugin's transform wrapper and the image and
 * overlay zoom and pan as one.
 *
 * Lice boxes are stored in the crop's original-crop pixel space (per the OBO endpoint), which is
 * exactly the loaded image's intrinsic size — not `observation.resolution` (the full-frame capture
 * resolution). So the overlay's coordinate space is the image's own `naturalWidth`/`naturalHeight`,
 * read on load; until the image loads there is nothing to draw anyway.
 */
export const FishObservationSlideView = ({
  slide,
  zoom,
  annotationTypes,
}: FishObservationSlideViewProps) => {
  const { t } = useTranslation('orbitOne');
  const { src, alt, observation, fetchingStatus, fullResolutionState } = slide;
  const imageRef = useRef<HTMLImageElement>(null);
  const [loadedImageSize, setLoadedImageSize] = useState<[number, number] | undefined>();
  const boxes = resolveAnnotationBoxes({ observation, annotationTypes });

  useEffect(() => {
    setLoadedImageSize(undefined);
    const img = imageRef.current;
    if (img?.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setLoadedImageSize([img.naturalWidth, img.naturalHeight]);
    }
  }, [/* effect dep */ src]);

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setLoadedImageSize([naturalWidth, naturalHeight]);
    }
  };

  return (
    <FlexAligner fullHeight alignment='center'>
      <Loader
        render={fetchingStatus === 'loading'}
        backgroundColor={colors.palette.neutral.dark['700']}
      />
      <div style={imageBoxStyle}>
        <img
          ref={imageRef}
          src={src}
          alt={alt ?? ''}
          style={imageStyle}
          draggable={false}
          onLoad={handleImageLoad}
        />
        {/* Boxes are in full-crop pixels; skip overlay while the lightbox still shows the thumb. */}
        {fullResolutionState === 'ready' && (
          <OrbitOneAnnotationOverlay boxes={boxes} cropSize={loadedImageSize} zoom={zoom} />
        )}
        {fullResolutionState === 'error' && (
          <Alert
            severity='critical'
            text={t('liceCount.imageGallery.cannotLoadImage')}
            style={fullResolutionErrorStyle}
          />
        )}
      </div>
    </FlexAligner>
  );
};
